import React from 'react';
import { FaShieldAlt, FaUsersCog, FaHandshake, FaEye, FaSyncAlt, FaMapMarkerAlt, FaExternalLinkAlt } from 'react-icons/fa';
import { motion } from 'motion/react';
import ScrollReveal from './ScrollReveal';
import PageNavbar from './PageNavbar';
import {
  defaultViewport,
  staggerContainer,
  staggerItem,
  getRevealProps,
} from '../motion/scrollMotion';
import Footer from './Footer';
import './AboutPage.css';

const stats = [
  { value: '500+', label: 'Security Assessments Completed' },
  { value: 'SLA-Backed', label: 'Managed IT Uptime' },
  { value: '100%', label: 'Certified Security Team' },
  { value: '20+', label: 'Years of Leadership Experience' },
];

const values = [
  {
    icon: <FaShieldAlt />,
    title: 'Security First',
    desc: 'Security is not an add-on — it is fundamental to everything we design, build, and manage.',
  },
  {
    icon: <FaUsersCog />,
    title: 'Technical Excellence',
    desc: 'We hire certified professionals and invest in continuous skill development across all verticals.',
  },
  {
    icon: <FaHandshake />,
    title: 'Partnership Mindset',
    desc: 'We act as an extension of our clients\' teams, aligned with their business goals and operational realities.',
  },
  {
    icon: <FaEye />,
    title: 'Transparency',
    desc: 'We communicate clearly, report honestly, and earn trust through consistent delivery.',
  },
  {
    icon: <FaSyncAlt />,
    title: 'Continuous Improvement',
    desc: 'We iterate, evolve, and refine — because the threat landscape and technology never stand still.',
  },
];

const AboutPage = () => {
  return (
    <div className="about-page">
      <PageNavbar />

      {/* About Hero */}
      <section className="about-hero">
        <div className="about-hero-background"></div>
        <div className="about-hero-stars"></div>
        <ScrollReveal className="about-hero-content" animateOnMount variant="fadeUp" duration={1.05}>
          <span className="about-badge">About Proven InfoSec</span>
          <h1 className="about-hero-title">
            Securing, Managing, and Building<br />
            Technology That <span className="about-accent">Lasts.</span>
          </h1>
          <p className="about-hero-description">
            Proven InfoSec helps organizations secure, manage, and modernize technology
            environments by combining cybersecurity expertise, IT operations capability,
            and secure engineering execution.
          </p>
        </ScrollReveal>
      </section>

      {/* Key Metrics */}
      <section className="about-metrics-section">
        <div className="about-container">
          <motion.div className="about-metrics-grid" {...getRevealProps('fadeUp')}>
            {stats.map((s, i) => (
              <div className="about-metric-card" key={i}>
                <div className="about-metric-value">{s.value}</div>
                <div className="about-metric-label">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="about-mv-section">
        <div className="about-container">
          <motion.div className="about-mv-block" {...getRevealProps('slideLeft')}>
            <span className="about-block-tag">Our Mission</span>
            <h2 className="about-block-title">Empowering Organizations Through Secure Technology</h2>
            <div className="about-block-bodies">
              <p className="about-block-body">
                In a world where cyber threats continue to grow in complexity and scale,
                organizations need a technology partner they can trust. Proven InfoSec exists
                to provide that partnership — delivering cybersecurity, managed IT, and secure
                software development under one roof.
              </p>
              <p className="about-block-body">
                We help mid-market and enterprise organizations protect critical assets,
                maintain operational stability, and build secure digital products that drive
                business value.
              </p>
            </div>
          </motion.div>

          <motion.div className="about-mv-block reverse" {...getRevealProps('slideRight')}>
            <span className="about-block-tag">Our Vision</span>
            <h2 className="about-block-title">A Unified Approach to Technology Services</h2>
            <div className="about-block-bodies">
              <p className="about-block-body">
                Most organizations engage separate vendors for cybersecurity, IT operations,
                and software development. This fragmented approach creates gaps, inconsistencies,
                and blind spots that attackers exploit.
              </p>
              <p className="about-block-body">
                Proven InfoSec's integrated delivery model ensures security is embedded into
                IT operations and software engineering from the start — not bolted on as an
                afterthought.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Values */}
      <section className="about-values-section">
        <div className="about-container">
          <motion.div className="about-values-header" {...getRevealProps('fadeUp')}>
            <span className="about-block-tag">Our Values</span>
            <h2 className="about-values-title">
              The Principles That Guide Every Engagement
            </h2>
            <p className="about-values-subtitle">
              How we work matters as much as what we deliver. These five values shape every
              client relationship, every assessment, and every line of code.
            </p>
          </motion.div>

          <motion.div
            className="about-values-grid"
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
            variants={staggerContainer}
          >
            {values.map((v, i) => (
              <motion.div className="about-value-card" key={i} variants={staggerItem}>
                <div className="about-value-icon">{v.icon}</div>
                <h3 className="about-value-title">{v.title}</h3>
                <p className="about-value-desc">{v.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Office Locations */}
      <section className="about-offices-section">
        <div className="about-container">
          <motion.div className="about-offices-header" {...getRevealProps('fadeUp')}>
            <span className="about-block-tag">Where We Operate</span>
            <h2 className="about-offices-title">Two Offices, One Integrated Team</h2>
            <p className="about-offices-subtitle">
              Service coverage across the United Kingdom and India — supporting 24/7 SOC
              operations and one business day response on every inquiry.
            </p>
          </motion.div>

          <div className="about-offices-grid">
            <motion.div className="about-office-card" {...getRevealProps('fadeUp')}>
              <div className="about-office-flag">🇬🇧</div>
              <h3 className="about-office-region">United Kingdom Office</h3>
              <div className="about-office-address">
                <FaMapMarkerAlt className="about-office-icon" />
                <div>
                  Unit 5, Mansfield House, 22 Northgate<br />
                  Sleaford, NG34 7DA<br />
                  United Kingdom
                </div>
              </div>
              <div className="about-office-coords">52.99636°N · 0.40766°W</div>
              <a href="https://maps.app.goo.gl/fvH8KGydrmkMgFw49" target="_blank" rel="noopener noreferrer" className="about-office-map">
                View on Google Maps <FaExternalLinkAlt />
              </a>
            </motion.div>

            <motion.div className="about-office-card" {...getRevealProps('fadeUp', 0.15)}>
              <div className="about-office-flag">🇮🇳</div>
              <h3 className="about-office-region">India Office</h3>
              <div className="about-office-address">
                <FaMapMarkerAlt className="about-office-icon" />
                <div>
                  Pattupura Puzhakkaradath Building<br />
                  Kakkanad, Kochi, Kerala 682030<br />
                  India
                </div>
              </div>
              <div className="about-office-coords">10.01580°N · 76.35210°E</div>
              <a href="https://maps.app.goo.gl/ZEV1ThCeXsNj1wYv7" target="_blank" rel="noopener noreferrer" className="about-office-map">
                View on Google Maps <FaExternalLinkAlt />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
